// src/pages/clients/ImportMikrotikClients.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    FormControl,
    Select,
    MenuItem,
    TextField,
    InputLabel,
    CircularProgress,
    Alert,
    Grid,
    Checkbox,
    FormControlLabel,
    Divider,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    AlertTitle,
    Stepper,
    Step,
    StepLabel,
    Chip,
    IconButton,
    LinearProgress
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    ImportExport as ImportIcon,
    Check as CheckIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    NavigateNext as NextIcon,
    NavigateBefore as PrevIcon,
    Settings as SettingsIcon,
    Router as RouterIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const parseQueueName = (name) => {
    if (!name) return { firstName: '', lastName: '' };
    const parts = name.split(/[-_\s]+/);
    if (parts.length >= 2) {
        const firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
        const lastName = parts.slice(1)
            .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
            .join(' ');
        return { firstName, lastName };
    } else if (parts.length === 1 && parts[0]) {
        const firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
        return { firstName, lastName: '' };
    }
    return { firstName: '', lastName: '' };
};

const ImportMikrotikClients = () => {
    // Estados básicos
    const [routers, setRouters] = useState([]);
    const [zones, setZones] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [accessPoints, setAccessPoints] = useState([]);
    const [selectedRouter, setSelectedRouter] = useState('');
    const [selectedZone, setSelectedZone] = useState('');
    const [selectedNode, setSelectedNode] = useState('');
    const [selectedAccessPoint, setSelectedAccessPoint] = useState('');
    const [defaultPassword, setDefaultPassword] = useState('');
    const [generateQueueName, setGenerateQueueName] = useState(false);

    const [loading, setLoading] = useState(false);
    const [queuesLoading, setQueuesLoading] = useState(false);
    const [queues, setQueues] = useState([]);
    const [error, setError] = useState('');

    // Estado del Stepper (Paso Activo)
    const [activeStep, setActiveStep] = useState(0);

    // Mapa de selección
    const [selectedItems, setSelectedItems] = useState(new Map());

    // Paginación
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Estado para modal de resultados
    const [importResults, setImportResults] = useState(null);
    const [resultsModalOpen, setResultsModalOpen] = useState(false);

    // Cargar datos iniciales
    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [routersRes, zonesRes, nodesRes, apRes] = await Promise.all([
                fetch('/api/routers', {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }),
                fetch('/api/zones', {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }),
                fetch('/api/nodes', {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }),
                fetch('/api/access-points', {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                })
            ]);

            const [routersData, zonesData, nodesData, apData] = await Promise.all([
                routersRes.json(),
                zonesRes.json(),
                nodesRes.json(),
                apRes.json()
            ]);

            setRouters(routersData);
            setZones(zonesData);
            setNodes(nodesData);
            setAccessPoints(apData);
        } catch (error) {
            console.error('Error fetching initial data:', error);
            setError('Error al cargar datos iniciales');
        } finally {
            setLoading(false);
        }
    };

    const handleZoneChange = (e) => {
        const zoneId = e.target.value;
        setSelectedZone(zoneId);
        setSelectedRouter('');
        setSelectedNode('');
        setSelectedAccessPoint('');
        setQueues([]);
        setSelectedItems(new Map());
    };

    const handleRouterChange = (e) => {
        setSelectedRouter(e.target.value);
        setSelectedAccessPoint('');
        setQueues([]);
        setSelectedItems(new Map());
    };

    const handleNodeChange = (e) => {
        setSelectedNode(e.target.value);
        setSelectedAccessPoint('');
    };

    const filteredRouters = selectedZone
        ? routers.filter(r => r.zone_id === selectedZone)
        : [];

    const filteredNodes = selectedZone
        ? nodes.filter(n => n.zone_id === selectedZone)
        : [];

    const filteredAccessPoints = selectedNode && selectedRouter
        ? accessPoints.filter(ap => ap.node_id === selectedNode && ap.router_id === selectedRouter)
        : [];

    // Cargar queues del router seleccionado
    const fetchQueues = async () => {
        if (!selectedRouter) {
            setError('Seleccione un router');
            return;
        }

        setQueuesLoading(true);
        setError('');
        setQueues([]);
        setSelectedItems(new Map());

        try {
            const response = await fetch(`/api/import/queues/${selectedRouter}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al obtener Simple Queues');
            }

            const queuesData = await response.json();
            
            // Asignar un índice único a cada queue y parsear nombres automáticamente
            const processedQueues = queuesData.map((queue, index) => {
                const parsed = parseQueueName(queue.name);
                return {
                    ...queue,
                    firstName: parsed.firstName,
                    lastName: parsed.lastName,
                    uniqueId: `${queue.ipAddress}-${index}`
                };
            });

            setQueues(processedQueues);
            
            // Avanzar automáticamente al paso 1 (Configuración) al cargar con éxito
            setActiveStep(1);
        } catch (error) {
            console.error('Error fetching queues:', error);
            setError(error.message || 'Error al obtener Simple Queues');
        } finally {
            setQueuesLoading(false);
        }
    };

    // Edición de nombres
    const handleNameChange = (uniqueId, field, value) => {
        setQueues(prevQueues => 
            prevQueues.map(q => 
                q.uniqueId === uniqueId ? { ...q, [field]: value } : q
            )
        );
    };

    // Selección individual
    const handleItemSelection = (uniqueId, isSelected) => {
        const newSelection = new Map(selectedItems);
        if (isSelected) {
            newSelection.set(uniqueId, true);
        } else {
            newSelection.delete(uniqueId);
        }
        setSelectedItems(newSelection);
    };

    // Seleccionar todos en la página
    const handleSelectAllInPage = (select) => {
        const newSelection = new Map(selectedItems);
        const visibleQueues = queues.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        visibleQueues.forEach(queue => {
            if (!queue.exists) {
                if (select) {
                    newSelection.set(queue.uniqueId, true);
                } else {
                    newSelection.delete(queue.uniqueId);
                }
            }
        });

        setSelectedItems(newSelection);
    };

    // Formateo de velocidad
    const formatSpeedDisplay = (speed) => {
        if (!speed) return '-';
        if (typeof speed === 'string' && (
            speed.includes('M') ||
            speed.includes('m') ||
            speed.includes('K') ||
            speed.includes('k') ||
            speed === '0/0' ||
            speed === 'unlimited'
        )) {
            return speed.toUpperCase();
        }

        try {
            const parts = speed.split('/');
            if (parts.length !== 2) return speed;

            const formattedParts = parts.map(part => {
                const numValue = parseInt(part);
                if (isNaN(numValue)) return part;

                if (numValue >= 1000000) {
                    return `${Math.round(numValue / 1000000)}M`;
                }
                else if (numValue >= 1000) {
                    return `${Math.round(numValue / 1000)}K`;
                }
                return part;
            });

            return formattedParts.join('/');
        } catch (e) {
            console.warn('Error al formatear velocidad:', speed, e);
            return speed;
        }
    };

    // Paginación handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Importar clientes
    const handleImportClients = async () => {
        const selectedQueueIds = Array.from(selectedItems.keys());

        if (selectedQueueIds.length === 0) {
            setError('Seleccione al menos una queue para importar');
            return;
        }

        if (!selectedZone) {
            setError('Seleccione una zona');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const selectedQueuesData = queues.filter(queue =>
                selectedItems.has(queue.uniqueId)
            );

            const response = await fetch('/api/import/queues', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    routerId: selectedRouter,
                    queues: selectedQueuesData,
                    zoneId: selectedZone,
                    defaultPassword: defaultPassword || null,
                    generateQueueName,
                    accessPointId: selectedAccessPoint || null
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al importar clientes');
            }

            const results = await response.json();
            setImportResults(results);
            setResultsModalOpen(true);

            // Refrescar y reiniciar
            fetchQueues();
            setActiveStep(0);
        } catch (error) {
            console.error('Error importing clients:', error);
            setError(error.message || 'Error al importar clientes');
        } finally {
            setLoading(false);
        }
    };

    // Navegación del Stepper
    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    // Variables de control de selección
    const visibleRows = queues.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const numSelectable = visibleRows.filter(queue => !queue.exists).length;
    const numSelected = visibleRows.filter(queue => !queue.exists && selectedItems.has(queue.uniqueId)).length;
    const allSelected = numSelectable > 0 && numSelected === numSelectable;
    const someSelected = numSelected > 0 && numSelected < numSelectable;
    const totalSelected = selectedItems.size;

    return (
        <Box className="p-6" sx={{ maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header Modernizado */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            fontWeight: 'bold', 
                            background: 'linear-gradient(45deg, #1e3c72 30%, #2a5298 90%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1
                        }}
                    >
                        Importar Clientes desde MikroTik
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Sincroniza y crea cuentas de clientes en base a tus colas simples de MikroTik.
                    </Typography>
                </Box>
                {selectedRouter && (
                    <Chip 
                        icon={<RouterIcon />} 
                        label={`Router: ${routers.find(r => r.id === selectedRouter)?.name || 'Desconectado'}`} 
                        color="primary"
                        variant="outlined"
                        sx={{ borderRadius: '8px', fontWeight: 'medium' }}
                    />
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Stepper Superior */}
            <Paper sx={{ p: 3, mb: 4, borderRadius: '16px', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    <Step>
                        <StepLabel>Conectar Router</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Configurar Parámetros</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Revisar e Importar ({queues.length})</StepLabel>
                    </Step>
                </Stepper>
            </Paper>

            {/* PASO 1: Conexión y Carga de Colas */}
            {activeStep === 0 && (
                <Grid container justifyContent="center">
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{ p: 1, borderRadius: '8px', bgcolor: 'primary.light', mr: 2, display: 'flex', alignItems: 'center' }}>
                                        <RouterIcon color="primary" />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            Paso 1: Conexión con MikroTik
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Selecciona la zona y el router MikroTik del que deseas extraer las Simple Queues.
                                        </Typography>
                                    </Box>
                                </Box>

                                <Grid container spacing={3} sx={{ mb: 4 }}>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>Zona de Operación</InputLabel>
                                            <Select
                                                value={selectedZone}
                                                onChange={handleZoneChange}
                                                label="Zona de Operación"
                                                sx={{ borderRadius: '10px' }}
                                            >
                                                <MenuItem value="">Seleccione una zona</MenuItem>
                                                {zones.map((zone) => (
                                                    <MenuItem key={zone.id} value={zone.id}>
                                                        {zone.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth disabled={!selectedZone}>
                                            <InputLabel>Router MikroTik</InputLabel>
                                            <Select
                                                value={selectedRouter}
                                                onChange={handleRouterChange}
                                                label="Router MikroTik"
                                                sx={{ borderRadius: '10px' }}
                                            >
                                                <MenuItem value="">Seleccione un router</MenuItem>
                                                {filteredRouters.map((router) => (
                                                    <MenuItem key={router.id} value={router.id}>
                                                        {router.name} ({router.ip})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    {queues.length > 0 && (
                                        <Button
                                            variant="outlined"
                                            onClick={() => setActiveStep(1)}
                                            endIcon={<NextIcon />}
                                            sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
                                        >
                                            Siguiente
                                        </Button>
                                    )}
                                    <Button
                                        variant="contained"
                                        startIcon={queuesLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                                        onClick={fetchQueues}
                                        disabled={!selectedRouter || queuesLoading}
                                        sx={{ 
                                            borderRadius: '8px', 
                                            textTransform: 'none', 
                                            px: 4, 
                                            boxShadow: '0 4px 12px 0 rgba(30, 60, 114, 0.2)' 
                                        }}
                                    >
                                        {queuesLoading ? 'Conectando...' : 'Conectar y Cargar Colas'}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* PASO 2: Configurar Parámetros */}
            {activeStep === 1 && (
                <Grid container justifyContent="center">
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{ p: 1, borderRadius: '8px', bgcolor: 'primary.light', mr: 2, display: 'flex', alignItems: 'center' }}>
                                        <SettingsIcon color="primary" />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            Paso 2: Parámetros de Importación
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Configura los nodos, APs y contraseñas por defecto que se asignarán a los clientes importados.
                                        </Typography>
                                    </Box>
                                </Box>

                                <Grid container spacing={3} sx={{ mb: 4 }}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Zona de Importación Seleccionada"
                                            value={selectedZone ? (zones.find(z => z.id === selectedZone)?.name || '') : 'Ninguna'}
                                            InputProps={{ readOnly: true }}
                                            disabled
                                            sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Contraseña por defecto (opcional)"
                                            type="password"
                                            value={defaultPassword}
                                            onChange={(e) => setDefaultPassword(e.target.value)}
                                            helperText="Contraseña para acceso al portal del cliente"
                                            sx={{ '& .MuiInputBase-root': { borderRadius: '10px' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth disabled={!selectedZone}>
                                            <InputLabel>Torre / Nodo (Opcional)</InputLabel>
                                            <Select
                                                value={selectedNode}
                                                onChange={handleNodeChange}
                                                label="Torre / Nodo (Opcional)"
                                                sx={{ borderRadius: '10px' }}
                                            >
                                                <MenuItem value="">Ninguna / Sin Nodo</MenuItem>
                                                {filteredNodes.map((node) => (
                                                    <MenuItem key={node.id} value={node.id}>
                                                        {node.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth disabled={!selectedNode || !selectedRouter}>
                                            <InputLabel>AP / Sectorial (Opcional)</InputLabel>
                                            <Select
                                                value={selectedAccessPoint}
                                                onChange={(e) => setSelectedAccessPoint(e.target.value)}
                                                label="AP / Sectorial (Opcional)"
                                                sx={{ borderRadius: '10px' }}
                                            >
                                                <MenuItem value="">Ninguna / Sin AP</MenuItem>
                                                {filteredAccessPoints.map((ap) => (
                                                    <MenuItem key={ap.id} value={ap.id}>
                                                        {ap.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '10px', bgcolor: 'grey.50' }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={generateQueueName}
                                                        onChange={(e) => setGenerateQueueName(e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                            Generar nombres de colas automáticamente
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Si se activa, el nombre de la cola en el sistema se creará basándose en la IP (ej. client-192-168-1-50) en lugar de usar el nombre original de MikroTik.
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<PrevIcon />}
                                        onClick={handleBack}
                                        sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
                                    >
                                        Atrás
                                    </Button>
                                    <Button
                                        variant="contained"
                                        endIcon={<NextIcon />}
                                        onClick={handleNext}
                                        sx={{ borderRadius: '8px', textTransform: 'none', px: 4 }}
                                    >
                                        Continuar a Revisión
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* PASO 3: Selección e Importación de Clientes */}
            {activeStep === 2 && (
                <Paper sx={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 30px rgba(0,0,0,0.06)' }}>
                    <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.50', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Paso 3: Revisión de Colas de MikroTik
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Edita los nombres/apellidos si es necesario, marca los clientes que deseas importar y presiona "Importar Clientes".
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<PrevIcon />}
                                onClick={handleBack}
                                disabled={loading}
                                sx={{ borderRadius: '8px', textTransform: 'none' }}
                            >
                                Atrás
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ImportIcon />}
                                onClick={handleImportClients}
                                disabled={totalSelected === 0 || loading}
                                sx={{ 
                                    borderRadius: '8px', 
                                    textTransform: 'none', 
                                    px: 3,
                                    boxShadow: '0 4px 12px 0 rgba(30, 60, 114, 0.2)' 
                                }}
                            >
                                {loading ? 'Importando...' : `Importar ${totalSelected} Seleccionados`}
                            </Button>
                        </Box>
                    </Box>

                    {loading && <LinearProgress color="primary" />}

                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: 'grey.100' }}>
                                <TableRow>
                                    <TableCell padding="checkbox" sx={{ fontWeight: 'bold' }}>
                                        <Checkbox
                                            indeterminate={someSelected}
                                            checked={allSelected}
                                            onChange={(e) => handleSelectAllInPage(e.target.checked)}
                                            disabled={numSelectable === 0}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Cola Simple (MikroTik)</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Nombre (Cliente)</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Apellido (Cliente)</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Dirección IP</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Velocidad (U/D)</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Comentario</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleRows.map((queue) => {
                                    const isSelected = selectedItems.has(queue.uniqueId);
                                    const isExisting = queue.exists;

                                    return (
                                        <TableRow
                                            hover
                                            key={queue.uniqueId}
                                            selected={isSelected}
                                            sx={{ 
                                                opacity: isExisting ? 0.6 : 1,
                                                bgcolor: isExisting ? 'action.hover' : 'inherit'
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={(e) => handleItemSelection(queue.uniqueId, e.target.checked)}
                                                    disabled={isExisting}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>
                                                {queue.name}
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    value={queue.firstName || ''}
                                                    onChange={(e) => handleNameChange(queue.uniqueId, 'firstName', e.target.value)}
                                                    disabled={isExisting}
                                                    placeholder="Nombre"
                                                    sx={{ 
                                                        width: 150,
                                                        '& .MuiInputBase-root': { borderRadius: '6px' }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    value={queue.lastName || ''}
                                                    onChange={(e) => handleNameChange(queue.uniqueId, 'lastName', e.target.value)}
                                                    disabled={isExisting}
                                                    placeholder="Apellido"
                                                    sx={{ 
                                                        width: 150,
                                                        '& .MuiInputBase-root': { borderRadius: '6px' }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>
                                                {queue.ipAddress}
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={formatSpeedDisplay(`${queue.uploadSpeed}/${queue.downloadSpeed}`)} 
                                                    size="small" 
                                                    variant="outlined"
                                                    sx={{ fontWeight: 'medium' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {queue.comment ? (
                                                    <Tooltip title={queue.comment}>
                                                        <span>{queue.comment}</span>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" color="text.disabled">Sin comentario</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {isExisting ? (
                                                    <Chip 
                                                        icon={<ErrorIcon />} 
                                                        label="Ya Existe" 
                                                        color="warning" 
                                                        size="small"
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                ) : (
                                                    <Chip 
                                                        icon={queue.disabled ? <ErrorIcon /> : <CheckCircleIcon />} 
                                                        label={queue.disabled ? "Inactivo MT" : "Activo MT"} 
                                                        color={queue.disabled ? "error" : "success"} 
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {queues.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                            <Typography color="text.secondary">
                                                No hay Simple Queues cargadas en esta sesión.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={queues.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}

            {/* Modal de resultados */}
            <Dialog
                open={resultsModalOpen}
                onClose={() => setResultsModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '16px', p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" /> Resultados de la Importación
                </DialogTitle>
                <DialogContent>
                    {importResults && (
                        <Box sx={{ mt: 1 }}>
                            <Alert severity="success" sx={{ mb: 3, borderRadius: '10px' }}>
                                El proceso de importación del router ha concluido con éxito.
                            </Alert>
                            
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                                Resumen del Proceso:
                            </Typography>
                            
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={4}>
                                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: '10px' }}>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                            {importResults.success}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">Importados</Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={4}>
                                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: '10px' }}>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                                            {importResults.skipped}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">Omitidos</Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={4}>
                                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: '10px' }}>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                            {importResults.failures}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">Fallidos</Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Gestión de Tarifas y Velocidades:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                - Planes coincidentes vinculados: <strong>{importResults.matchedPlans || 0}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                - Planes nuevos auto-creados: <strong>{importResults.createdPlans || 0}</strong>
                            </Typography>

                            {importResults.failedQueues && importResults.failedQueues.length > 0 && (
                                <Box className="mt-4">
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main', mb: 1 }}>
                                        Detalles de Errores:
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 200, border: '1px solid', borderColor: 'grey.200', borderRadius: '8px' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Nombre</TableCell>
                                                    <TableCell>IP</TableCell>
                                                    <TableCell>Causa</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {importResults.failedQueues.map((queue, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell sx={{ fontFamily: 'monospace' }}>{queue.name}</TableCell>
                                                        <TableCell sx={{ fontFamily: 'monospace' }}>{queue.ipAddress}</TableCell>
                                                        <TableCell sx={{ color: 'error.main', fontSize: '0.8rem' }}>{queue.error}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setResultsModalOpen(false)}
                        variant="contained"
                        sx={{ borderRadius: '8px', px: 3 }}
                    >
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ImportMikrotikClients;